'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Select } from '../select';
const CompactFormContext = createContext(null);
export function CompactForm({ children, onSubmit, className = '', id }) {
    const formRef = useRef(null);
    // Handle keyboard navigation for compact layout
    const handleFieldKeyDown = (e, fieldIndex) => {
        const form = formRef.current;
        if (!form)
            return;
        const allFields = form.querySelectorAll('input, select, textarea');
        const totalFields = allFields.length;
        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                if (fieldIndex < totalFields - 1) {
                    const nextField = allFields[fieldIndex + 1];
                    nextField?.focus();
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (fieldIndex < totalFields - 1) {
                    const nextField = allFields[fieldIndex + 1];
                    nextField?.focus();
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (fieldIndex > 0) {
                    const prevField = allFields[fieldIndex - 1];
                    prevField?.focus();
                }
                break;
        }
    };
    const contextValue = {
        handleFieldKeyDown,
        formRef
    };
    return (_jsx(CompactFormContext.Provider, { value: contextValue, children: _jsx("form", { ref: formRef, onSubmit: onSubmit, className: `space-y-0 ${className}`, id: id, children: children }) }));
}
export function CompactField({ label, name, type = 'text', placeholder, required = false, value, onChange, fieldIndex, autoFocus = false, disabled = false, className = '', labelWidth = 'w-32', ...props }) {
    const context = useContext(CompactFormContext);
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    if (!context) {
        throw new Error('CompactField must be used within a CompactForm');
    }
    const { handleFieldKeyDown } = context;
    const inputClasses = `w-full md:w-[35%] h-6 px-2 text-xs border rounded transition-all duration-150 
    ${isFocused
        ? 'border-primary-500 ring-1 ring-primary-200 dark:ring-primary-800 bg-primary-50 dark:bg-primary-900/20'
        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'} 
    ${disabled ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'dark:text-gray-100'}
    focus:outline-none ${className}`;
    const labelClasses = `${labelWidth} text-xs font-medium text-right pr-3 py-1 transition-colors duration-150 flex-shrink-0
    ${isFocused
        ? 'text-primary-700 dark:text-primary-400'
        : 'text-gray-700 dark:text-gray-300'}`;
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);
    const handleKeyDown = (e) => {
        handleFieldKeyDown(e, fieldIndex);
    };
    return (_jsxs("div", { className: "flex flex-col md:flex-row md:items-center mb-px pb-1", children: [_jsxs("label", { className: labelClasses, children: [label, required && _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), type === 'checkbox' ? (_jsx("div", { className: "w-full md:w-[35%]", children: _jsx("input", { ref: inputRef, type: "checkbox", name: name, checked: value, onChange: onChange, onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, disabled: disabled, className: "w-4 h-4 text-primary-600 rounded border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:ring-2 bg-white dark:bg-gray-700", ...props }) })) : (_jsx("input", { ref: inputRef, type: type, name: name, value: value || '', onChange: onChange, placeholder: placeholder, className: inputClasses, onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), "data-field-index": fieldIndex, required: required, disabled: disabled, ...props }))] }));
}
export function CompactSelectField({ label, name, options, value, onChange, fieldIndex, placeholder = 'Select...', required = false, disabled = false, isSearchable = false, isClearable = false, labelWidth = 'w-32', className = '' }) {
    const [isFocused, setIsFocused] = useState(false);
    const labelClasses = `${labelWidth} text-xs font-medium text-right pr-3 py-1 transition-colors duration-150 flex-shrink-0
    ${isFocused
        ? 'text-primary-700 dark:text-primary-400'
        : 'text-gray-700 dark:text-gray-300'}`;
    return (_jsxs("div", { className: "flex flex-col md:flex-row md:items-center mb-px pb-1", children: [_jsxs("label", { className: labelClasses, children: [label, required && _jsx("span", { className: "text-red-500 ml-0.5", children: "*" })] }), _jsx("div", { className: "w-full md:w-[35%]", children: _jsx(Select, { placeholder: placeholder, options: options, value: value, onChange: onChange, className: `text-xs ${className}`, styles: {
                        control: (base) => ({
                            ...base,
                            minHeight: '24px',
                            height: '24px',
                            fontSize: '12px'
                        }),
                        valueContainer: (base) => ({
                            ...base,
                            height: '24px',
                            padding: '0 8px'
                        }),
                        input: (base) => ({
                            ...base,
                            margin: '0px'
                        }),
                        indicatorsContainer: (base) => ({
                            ...base,
                            height: '24px'
                        })
                    }, isSearchable: isSearchable, isClearable: isClearable, isDisabled: disabled, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false) }) })] }));
}
export function CompactFormSection({ title, children, className = '' }) {
    return (_jsxs("div", { className: `mb-2 ${className}`, children: [_jsx("h3", { className: "text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-1 pb-1 border-b border-gray-200 dark:border-gray-700", children: title }), _jsx("div", { className: "space-y-0", children: children })] }));
}
export function CompactFormToggle({ isCompact, onToggle, className = '' }) {
    return (_jsxs("div", { className: `flex items-center justify-between ${className}`, children: [_jsx("div", { className: "text-xs text-gray-600 dark:text-gray-400", children: "Form Layout" }), _jsx("button", { type: "button", onClick: () => onToggle(!isCompact), className: "flex items-center gap-2 px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors", children: isCompact ? (_jsxs(_Fragment, { children: [_jsx("svg", { className: "w-4 h-4 text-primary-600 dark:text-primary-400", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M9 5l7 7-7 7" }) }), _jsx("span", { className: "text-primary-600 dark:text-primary-400 font-medium", children: "Compact" })] })) : (_jsxs(_Fragment, { children: [_jsx("svg", { className: "w-4 h-4 text-gray-500 dark:text-gray-400", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15 19l-7-7 7-7" }) }), _jsx("span", { className: "text-gray-600 dark:text-gray-400", children: "Standard" })] })) })] }));
}
// CompactFormKeyboardHints component
export function CompactFormKeyboardHints({ className = '' }) {
    return (_jsxs("div", { className: `flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 ${className}`, children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "font-semibold", children: "\u21B5 Enter" }), " = Next Field"] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "font-semibold", children: "\u2191\u2193" }), " = Navigate"] })] }));
}
